import { Grid } from '@mui/material';
import { isDef, isDefAndNotNull } from '@utils/def';
import useDeepCallback from '@utils/useDeepCallback';
import { FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Sizes } from '../../../CheckBox/enums';
import FormField from '../../../FormField';
import { Sizes as SwitcherSizes } from '../../../Switcher/enums';

import { FormFieldCheckBoxStyles, FormFieldTypes } from '../../enums';
import {
  FormFieldCheckBoxValueType,
  FormFieldOption,
  FormFieldUnion,
  FormFieldValue,
  FormFields,
} from '../../types';
import {
  extractValue,
  getGridColumnAmount,
  getValidationRulesFromAField,
} from '../../utils';

import SchemaFormRenderer from '../SchemaFormRenderer';

import { StyledCheckBox, StyledSwitcher } from './styles';
import { Props } from './types';

const FormCheckBoxRenderer: FC<Props> = ({ field }) => {
  const { control, resetField } = useFormContext<FormFields>();
  const selectFieldValue = useWatch({ name: field.name });
  const validationRules = useMemo(
    () => getValidationRulesFromAField(field),
    [field],
  );
  const gridColumnAmount = useMemo(
    () => getGridColumnAmount(field.columnsAmount),
    [field.columnsAmount],
  );
  const gridOffsetColumnAmount = useMemo(
    () =>
      isDef(field.offsetColumnsAmount)
        ? getGridColumnAmount(field.offsetColumnsAmount)
        : undefined,
    [field.offsetColumnsAmount],
  );
  const dependantFields: FormFieldUnion[] = useMemo(() => {
    const fieldValue = extractValue(selectFieldValue || field.defaultValue);

    return (
      field?.dependantFields?.find((f) => f?.value === fieldValue)
        ?.dependantFields || []
    );
  }, [selectFieldValue, field.defaultValue, field?.dependantFields]);
  const optionFields: FormFieldOption[] = useMemo(
    () =>
      (field.dependantFields || []).filter(
        (p) => p.type === FormFieldTypes.option,
      ) as FormFieldOption[],
    [field.dependantFields],
  );
  const clearDependantFieldsDelayed = useCallback(
    (value: FormFieldValue) => {
      requestIdleCallback(() => {
        optionFields.forEach((option) => {
          const isActiveVariant = option.value === value;
          (option.dependantFields || []).forEach((f) => {
            if (isActiveVariant && isDefAndNotNull(f.defaultValue)) {
              resetField(f.name, { defaultValue: f.defaultValue });
            } else {
              resetField(f.name, { defaultValue: null });
            }
          });
        });
      });
    },
    [optionFields, resetField],
  );

  const handleCheckboxChange = useDeepCallback(
    (
      onChange: (value: boolean) => void,
      e: React.ChangeEvent,
      checked: boolean,
    ) => {
      const value = { label: checked.toString(), value: checked };
      clearDependantFieldsDelayed(extractValue(value) as FormFieldValue);
      onChange?.(checked);
    },
  );

  return (
    <>
      <Grid item xs={gridColumnAmount}>
        <FormField
          {...validationRules}
          defaultValue={field.defaultValue}
          controllerProps={{
            control,
            name: field.name,
            rules: field.validate ? { validate: field.validate } : undefined,
          }}
          render={(props) =>
            field.checkboxType === FormFieldCheckBoxStyles.switch ? (
              <StyledSwitcher
                {...props}
                onChange={handleCheckboxChange(props.onChange)}
                checked={Boolean(props.value as FormFieldCheckBoxValueType)}
                disabled={field.disabled}
                error={props.error}
                label={field.label}
                readOnly={field.readOnly}
                required={field.required}
                size={field.size as unknown as SwitcherSizes}
                styleType={field.styleType}
              />
            ) : (
              <StyledCheckBox
                {...props}
                onChange={handleCheckboxChange(props.onChange)}
                checked={Boolean(props.value as FormFieldCheckBoxValueType)}
                disabled={field.disabled}
                error={props.error}
                label={field.label}
                readOnly={field.readOnly}
                required={field.required}
                size={field.size as unknown as Sizes}
              />
            )
          }
        />
      </Grid>
      {isDef(gridOffsetColumnAmount) && (
        <Grid item xs={gridOffsetColumnAmount} />
      )}
      <SchemaFormRenderer fields={dependantFields} />
    </>
  );
};

export default FormCheckBoxRenderer;
